import { Bluetooth, CircleCheck, Laptop, Pencil, Radio, Wifi } from 'lucide-solid';
import { createEffect, createMemo, createSignal, For, onCleanup, Show, type JSX } from 'solid-js';
import { WifiFields } from '../components/WifiFields';
import { WebBluetoothControlClient, WebBluetoothProvisioner, type BluetoothProgress } from '../lib/bluetooth';
import { canRenameDevice } from '../lib/deviceRename';
import { isFreshHeartbeat, timeAgo } from '../lib/format';
import { hasAuthoredContinuousRuns, roomRigCounts, roomRigSummary } from '../lib/panelElements';
import { reportError } from '../lib/plainErrors';
import { controllerNetworkName, onWifiNetwork, validWifiPassword, wifiFailureMessage, type WifiStatus } from '../lib/wifi';
import { can } from '../lib/permissions.generated';
import { useVenue } from '../state/VenueProvider';
import { BusyButton, CardSection, ConfirmModal, ControlGate, EmptyState, InlineNotice, Modal, ScreenHeader } from '../components/ui';
import { TEST_IDS } from '../testIds';

export function ConnectScreen(): JSX.Element {
  const venue = useVenue();
  // Wi-Fi and pairing are `network.manage` / `pairing.manage` — manager+ (the ratified matrix: an operator
  // runs the room, never configures it). A console with NO cloud identity is the provisioning path: being
  // in the room with the setup code is its own authority, which is exactly why those commands never relay
  // through the cloud. So a signed-in person is judged by their venue role; a direct setup session is not.
  const canManageWifi = createMemo(() => (venue.user() ? can(venue.role(), 'network.manage') : true));
  const canManagePairing = createMemo(() => (venue.user() ? can(venue.role(), 'pairing.manage') : true));
  const [bluetoothOpen, setBluetoothOpen] = createSignal(false);
  const [bluetoothControlOpen, setBluetoothControlOpen] = createSignal(false);
  const [bluetoothControlCode, setBluetoothControlCode] = createSignal('');
  const [setupCode, setSetupCode] = createSignal('');
  const [setupSsid, setSetupSsid] = createSignal('');
  const [setupPassword, setSetupPassword] = createSignal('');
  const [setupOpenNetwork, setSetupOpenNetwork] = createSignal(false);
  const [bluetoothBusy, setBluetoothBusy] = createSignal(false);
  const [bluetoothProgress, setBluetoothProgress] = createSignal<BluetoothProgress>();
  const [wifiModalOpen, setWifiModalOpen] = createSignal(false);
  const [wifiSetupCode, setWifiSetupCode] = createSignal('');
  const [wifiSsid, setWifiSsid] = createSignal('');
  const [wifiPassword, setWifiPassword] = createSignal('');
  const [wifiOpenNetwork, setWifiOpenNetwork] = createSignal(false);
  const [wifiBusy, setWifiBusy] = createSignal(false);
  const [wifiStatus, setWifiStatus] = createSignal<WifiStatus>();
  const [wifiFeedback, setWifiFeedback] = createSignal<{ kind: 'joining' | 'connected' | 'failed'; message: string }>();
  const [forgetWifiOpen, setForgetWifiOpen] = createSignal(false);
  const allControllers = createMemo(() => venue.roomStatus()?.devices ?? []);
  /** A revoked controller belongs in staff tooling, never in an operator's room list. */
  const controllers = createMemo(() => allControllers().filter((controller) => controller.status !== 'revoked'));
  const canManageDevices = createMemo(() => canRenameDevice(venue.role()));
  const [renamingDeviceId, setRenamingDeviceId] = createSignal<string>();
  const [renameDraft, setRenameDraft] = createSignal('');
  const [renameError, setRenameError] = createSignal('');
  let renameInput!: HTMLInputElement;

  function beginRename(deviceId: string, displayName: string): void {
    setRenamingDeviceId(deviceId);
    setRenameDraft(displayName);
    setRenameError('');
    queueMicrotask(() => {
      renameInput?.focus();
      renameInput?.select();
    });
  }

  function cancelRename(): void {
    setRenamingDeviceId(undefined);
    setRenameDraft('');
    setRenameError('');
  }

  async function submitRename(event: SubmitEvent, deviceId: string): Promise<void> {
    event.preventDefault();
    const displayName = renameDraft().trim();
    if (!displayName) {
      setRenameError('Enter a controller name.');
      return;
    }
    const currentName = allControllers().find((controller) => controller.deviceId === deviceId)?.displayName?.trim();
    if (displayName === currentName) {
      cancelRename();
      return;
    }
    setRenameError('');
    const result = await venue.runAction(
      `rename-device-${deviceId}`,
      () => venue.renameDevice(deviceId, displayName),
      'Controller renamed',
    );
    if (result) cancelRename();
    else setRenameError('Name was not changed. Try again.');
  }

  const rigCounts = createMemo(() => roomRigCounts(
    hasAuthoredContinuousRuns(venue.roomLayout()) ? venue.roomModel() : undefined,
    venue.rig(),
  ));
  const mirrorOnline = createMemo(() => venue.mirror()?.heartbeat?.online && isFreshHeartbeat(venue.mirror()?.heartbeat?.lastSeenAt));
  /**
   * The network the controller is on, from its own mirror. A console with a direct link reads the live
   * `get_wifi_status` below instead — the same fact, closer to the truth — so this stands in only where
   * that read is unavailable, which is every hosted console.
   */
  const controllerNetwork = createMemo(() => controllerNetworkName(venue.mirror()?.network));

  async function beginBluetooth(): Promise<void> {
    if (!venue.selectedVenue() || !venue.selectedRoom()) return;
    setBluetoothBusy(true);
    setBluetoothProgress({ stage: 'preparing', message: 'Preparing a one-time claim' });
    try {
      const claim = await venue.cloudCall<{ code: string }>('generateClaimCode', {
        venueId: venue.selectedVenue()!.venueId,
        roomId: venue.selectedRoom()!.roomId,
      });
      const provisioner = new WebBluetoothProvisioner();
      provisioner.onProgress = setBluetoothProgress;
      const result = await provisioner.run({
        setupCode: setupCode().trim(),
        ssid: setupSsid().trim() || undefined,
        psk: setupOpenNetwork() ? '' : setupPassword(),
        security: setupOpenNetwork() ? 'open' : 'wpa2',
        claimCode: claim.code,
        appDeviceId: venue.lanClient.appDeviceId,
        displayName: `Web console · ${venue.user()?.email ?? 'operator'}`,
      });
      setBluetoothProgress({ stage: 'done', message: `${result.deviceName || 'Controller'} is ready`, detail: result.ip });
      // Bluetooth provisioning is the one path that can hand a hosted console a venue-network
      // pairing; the console itself no longer offers a place to type a controller address.
      if (result.ip) venue.lanClient.adoptPairing(`${result.ip}:8765`, result.lanToken);
      await venue.refreshCloud();
    } catch (error) {
      setBluetoothProgress({ stage: 'failed', message: reportError('bluetooth setup', error, 'Setup did not finish.').message });
    } finally {
      setBluetoothBusy(false);
    }
  }

  async function connectBluetoothControl(): Promise<void> {
    const connected = await venue.runAction(
      'connect-bluetooth',
      () => venue.connectBluetooth(bluetoothControlCode()),
      'Bluetooth control connected',
    );
    if (connected !== undefined || venue.bluetoothState() === 'connected') {
      setBluetoothControlCode('');
      setBluetoothControlOpen(false);
    }
  }

  function wifiDirectClient(): typeof venue.bluetoothClient | typeof venue.lanClient | undefined {
    if (venue.bluetoothState() === 'connected' && venue.bluetoothClient.connected) return venue.bluetoothClient;
    if (venue.lanState() === 'connected' && venue.lanHello()?.authenticated) return venue.lanClient;
    return undefined;
  }

  let wifiStatusRequest = 0;
  async function refreshWifiStatus(): Promise<void> {
    const request = ++wifiStatusRequest;
    const client = wifiDirectClient();
    if (!client) {
      setWifiStatus(undefined);
      return;
    }
    try {
      const status = await client.call<WifiStatus>('get_wifi_status');
      if (request === wifiStatusRequest) setWifiStatus(status);
    } catch {
      if (request === wifiStatusRequest) setWifiStatus(undefined);
    }
  }

  createEffect(() => {
    venue.bluetoothState();
    venue.lanState();
    void venue.lanHello()?.authenticated;
    void refreshWifiStatus();
  });

  const unsubscribeWifiNotifications = venue.bluetoothClient.subscribeNotifications((notification) => {
    if (notification.name === 'wifi_joining') {
      setWifiFeedback({ kind: 'joining', message: `Joining ${wifiSsid().trim()}…` });
    } else if (notification.name === 'wifi_got_ip') {
      const network = wifiSsid().trim();
      setWifiFeedback({ kind: 'connected', message: `Connected to ${network}` });
      setWifiStatus({ connected: true, ssid: network, interfaceKind: 'wifi', internet: true, wifiConnected: true });
    } else if (notification.name === 'wifi_failed') {
      setWifiFeedback({ kind: 'failed', message: wifiFailureMessage(notification.data?.reason) });
    }
  });
  onCleanup(unsubscribeWifiNotifications);

  function openWifiModal(): void {
    setWifiSetupCode('');
    setWifiSsid('');
    setWifiPassword('');
    setWifiOpenNetwork(false);
    setWifiFeedback(undefined);
    setWifiModalOpen(true);
  }

  async function joinWifi(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!venue.user() || !canManageWifi()) return;
    const network = wifiSsid().trim();
    if (!network || !validWifiPassword(wifiPassword(), wifiOpenNetwork())) return;
    setWifiBusy(true);
    setWifiFeedback(undefined);
    try {
      if (venue.bluetoothState() !== 'connected' || !venue.bluetoothClient.connected) {
        await venue.connectBluetooth(wifiSetupCode());
      }
      setWifiFeedback({ kind: 'joining', message: `Joining ${network}…` });
      await venue.bluetoothClient.call('provision_wifi', {
        ssid: network,
        psk: wifiOpenNetwork() ? '' : wifiPassword(),
        security: wifiOpenNetwork() ? 'open' : 'wpa2',
      }, 60_000);
      setWifiStatus({ connected: true, ssid: network, interfaceKind: 'wifi', internet: true, wifiConnected: true });
      setWifiFeedback({ kind: 'connected', message: `Connected to ${network}` });
      setWifiModalOpen(false);
      void refreshWifiStatus();
    } catch (error) {
      if (wifiFeedback()?.kind !== 'failed') {
        setWifiFeedback({ kind: 'failed', message: reportError('wifi', error, 'Could not join Wi-Fi').message });
      }
    } finally {
      setWifiBusy(false);
    }
  }

  async function forgetWifi(): Promise<void> {
    const current = wifiStatus();
    // On the network, not naming one: a hidden network has no SSID to test, and requiring one here is
    // what made this button dead on the Alchemy Pi. The controller is sent the real name it reported.
    if (!onWifiNetwork(current) || !venue.bluetoothClient.connected) return;
    const result = await venue.runAction<{ forgotten: boolean }>(
      'forget-wifi',
      () => venue.bluetoothClient.call('forget_wifi', { ssid: current!.ssid, force: true }),
    );
    if (result?.forgotten) {
      // Forgetting the Wi-Fi does not take a wired controller off the network — only its Wi-Fi goes.
      const wired = current!.interfaceKind === 'ethernet';
      setWifiStatus({
        connected: wired && current!.connected,
        ssid: '',
        interfaceKind: wired ? 'ethernet' : 'none',
        internet: wired && current!.internet,
        wifiConnected: false,
      });
      setForgetWifiOpen(false);
    }
  }

  const wifiJoinEnabled = createMemo(() => Boolean(
    wifiSsid().trim()
    && validWifiPassword(wifiPassword(), wifiOpenNetwork())
    && (venue.bluetoothClient.connected || wifiSetupCode().trim()),
  ));

  return (
    <>
      <ScreenHeader
        title="Connect"
        description="Choose the room’s controller or prepare this device for an internet outage."
        actions={
          <div class="button-row">
            <Show when={WebBluetoothControlClient.supported && canManagePairing()}>
              <button class="button button-primary" onClick={() => setBluetoothControlOpen(true)}><Bluetooth size={17} /> Connect over Bluetooth</button>
            </Show>
            <Show when={canManagePairing() && venue.user()}>
              <button class="button button-secondary" onClick={() => setBluetoothOpen(true)}><Bluetooth size={17} /> Set up controller</button>
            </Show>
          </div>
        }
      />

      <Show when={!WebBluetoothControlClient.supported}>
        <p class="bluetooth-support-note">Bluetooth control requires Chrome or Edge on desktop or Android; Safari and browsers on iPhone/iPad do not support Web Bluetooth.</p>
      </Show>

      <CardSection
        class="controller-list-surface"
        bodyClass="controller-list"
        heading={(
          <div class="section-heading">
            <div><h2>Controllers</h2></div>
          </div>
        )}
        tools={(
          <Show when={!venue.cloudReachable()}>
            <InlineNotice tone="warning" title="Internet connection lost">
              The controller list will return automatically. A paired venue-network connection remains available.
            </InlineNotice>
          </Show>
        )}
        footer={(
          <div class="cloud-summary">
            <div><span>Rig</span><strong data-testid={TEST_IDS.connectRigSummary}>{roomRigSummary(rigCounts())}</strong></div>
          </div>
        )}
      >
        <Show
          when={controllers().length > 0}
          fallback={<EmptyState icon={<Radio size={24} />} title="No controller in this room" detail="Set up a controller over Bluetooth, or choose another room." />}
        >
            <For each={controllers()}>
              {(controller) => {
                const selected = () => venue.selectedDeviceId() === controller.deviceId;
                const live = () => selected() && mirrorOnline();
                const displayName = () => controller.displayName || 'Venue controller';
                const renaming = () => renamingDeviceId() === controller.deviceId;
                const renameBusy = () => venue.actionBusy() === `rename-device-${controller.deviceId}`;
                return (
                  <div class="controller-row-shell">
                    <Show
                      when={renaming()}
                      fallback={
                        <>
                          <button
                            class="controller-row"
                            classList={{ selected: selected(), renamable: canManageDevices() }}
                            onClick={() => venue.selectController(controller.deviceId)}
                            data-device-id={controller.deviceId}
                          >
                            <span class={`controller-glyph ${live() ? 'online' : ''}`}><Laptop size={21} /></span>
                            <span class="controller-copy">
                              <strong>{displayName()}</strong>
                              <small>{live() ? 'Online now' : `Offline since ${timeAgo(controller.lastSeenAt)}`}</small>
                            </span>
                            <Show when={selected()}><CircleCheck size={20} class="selected-check" /></Show>
                          </button>
                          <Show when={canManageDevices()}>
                            <button
                              type="button"
                              class="controller-rename-trigger"
                              aria-label={`Rename ${displayName()}`}
                              onClick={() => beginRename(controller.deviceId, displayName())}
                            ><Pencil size={15} /></button>
                          </Show>
                        </>
                      }
                    >
                      <form
                        class="controller-row controller-rename-form"
                        classList={{ selected: selected(), invalid: Boolean(renameError()) }}
                        onSubmit={(event) => void submitRename(event, controller.deviceId)}
                      >
                        <span class={`controller-glyph ${live() ? 'online' : ''}`}><Laptop size={21} /></span>
                        <span class="controller-copy controller-rename-copy">
                          <input
                            ref={renameInput}
                            aria-label="Controller name"
                            data-testid={TEST_IDS.controllerNameInput}
                            maxlength={120}
                            value={renameDraft()}
                            disabled={renameBusy()}
                            onInput={(event) => setRenameDraft(event.currentTarget.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                cancelRename();
                              }
                            }}
                          />
                          <small classList={{ 'rename-error': Boolean(renameError()) }} role={renameError() ? 'alert' : undefined}>
                            {renameError() || 'Enter to save · Escape to cancel'}
                          </small>
                        </span>
                        <BusyButton busyKey={`rename-device-${controller.deviceId}`} class="rename-save" type="submit" disabled={!renameDraft().trim()}>Save</BusyButton>
                      </form>
                    </Show>
                  </div>
                );
              }}
            </For>
        </Show>
      </CardSection>

      <section class="surface wifi-card" data-testid={TEST_IDS.wifiCard}>
        <div class="section-heading">
          <div><h2>Wi-Fi</h2></div>
          <Show when={venue.user() && canManageWifi() && WebBluetoothControlClient.supported}>
            <button class="button button-primary" onClick={openWifiModal} data-testid={TEST_IDS.changeWifi}>Change Wi-Fi</button>
          </Show>
        </div>
        <Show
          when={wifiDirectClient()}
          fallback={
            <Show
              when={controllerNetwork()}
              fallback={<p class="wifi-unavailable">Connect over Bluetooth to change the controller's Wi-Fi</p>}
            >
              {(network) => <p class="wifi-network" data-testid={TEST_IDS.controllerNetwork}>{network()}</p>}
            </Show>
          }
        >
          <Show when={wifiStatus()} fallback={<p class="wifi-unavailable">Checking Wi-Fi…</p>}>
            {(status) => (
              <div class="wifi-status-row">
                <span classList={{ connected: status().connected }}><Wifi size={22} /></span>
                <div>
                  <strong data-testid={TEST_IDS.wifiStatus}>
                    {onWifiNetwork(status())
                      ? `Connected to ${status().ssid || 'this network'}`
                      : 'Not connected'}
                  </strong>
                  <Show when={status().connected}>
                    <small>{status().internet ? 'Internet available' : 'No internet'}</small>
                  </Show>
                </div>
                <div class="wifi-status-actions">
                  <Show when={onWifiNetwork(status()) && venue.user() && canManageWifi() && venue.bluetoothClient.connected}>
                    <button class="text-button danger-text" onClick={() => setForgetWifiOpen(true)}>Forget this network</button>
                  </Show>
                </div>
              </div>
            )}
          </Show>
        </Show>
      </section>

      <Show when={venue.bluetoothState() !== 'idle'}>
        <section class="surface bluetooth-control-card" data-testid={TEST_IDS.bluetoothControlState}>
          <span classList={{ connected: venue.bluetoothState() === 'connected' }}><Bluetooth size={21} /></span>
          <div>
            <small>Bluetooth control</small>
            <strong>{venue.bluetoothState() === 'connected' ? venue.bluetoothIdentity()?.deviceName || venue.bluetoothClient.deviceName || 'Controller connected' : venue.bluetoothState() === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}</strong>
            <Show when={venue.bluetoothReason()}><p>{venue.bluetoothReason()}</p></Show>
          </div>
          <Show when={venue.bluetoothState() === 'connected'}>
            <BusyButton busyKey="disconnect-bluetooth" class="button button-secondary" onClick={() => void venue.runAction('disconnect-bluetooth', venue.disconnectBluetooth, 'Bluetooth disconnected')}>Disconnect</BusyButton>
          </Show>
        </section>
      </Show>

      <Modal title="Set up a controller" open={bluetoothOpen()} onClose={() => !bluetoothBusy() && setBluetoothOpen(false)}>
        <ControlGate>
          <Show
            when={WebBluetoothProvisioner.supported}
            fallback={<InlineNotice tone="warning" title="Bluetooth isn’t available here">Use Chrome or Edge on desktop or Android. Safari and all browsers on iPhone/iPad do not support Web Bluetooth.</InlineNotice>}
          >
            <div class="setup-flow">
              <div class="setup-destination"><span><Radio size={18} /></span><div><small>Set up in</small><strong>{venue.selectedVenue()?.displayName} / {venue.selectedRoom()?.displayName}</strong></div></div>
              <label><span>Setup code</span><input autocomplete="one-time-code" value={setupCode()} onInput={(event) => setSetupCode(event.currentTarget.value)} placeholder="Setup code" /></label>
              <WifiFields
                ssid={setupSsid()}
                password={setupPassword()}
                openNetwork={setupOpenNetwork()}
                onSSID={setSetupSsid}
                onPassword={setSetupPassword}
                onOpenNetwork={setSetupOpenNetwork}
                legend={<legend>Wi-Fi details <span>if needed</span></legend>}
              />
              <Show when={bluetoothProgress()}>
                <div class={`setup-progress ${bluetoothProgress()?.stage === 'failed' ? 'failed' : ''}`}>
                  <span classList={{ pulse: bluetoothBusy() }}><Bluetooth size={19} /></span>
                  <div><strong>{bluetoothProgress()?.message}</strong><Show when={bluetoothProgress()?.detail}><small>{bluetoothProgress()?.detail}</small></Show></div>
                </div>
              </Show>
              <button class="button button-primary button-large" disabled={bluetoothBusy() || !setupCode().trim()} onClick={() => void beginBluetooth()}>
                <Bluetooth size={18} /> {bluetoothBusy() ? 'Setting up…' : 'Choose controller and begin'}
              </button>
              <p class="field-help">Your browser will ask which nearby Nuance Venue controller to use. The setup code secures Wi-Fi credentials and the one-time venue claim.</p>
            </div>
          </Show>
        </ControlGate>
      </Modal>

      <Modal title="Change Wi-Fi" open={wifiModalOpen()} onClose={() => !wifiBusy() && setWifiModalOpen(false)}>
        <ControlGate>
          <form class="setup-flow" onSubmit={(event) => void joinWifi(event)}>
            <Show when={!venue.bluetoothClient.connected}>
              <label>
                <span>Setup code</span>
                <input
                  type="password"
                  autocomplete="off"
                  required
                  value={wifiSetupCode()}
                  onInput={(event) => setWifiSetupCode(event.currentTarget.value)}
                  data-testid={TEST_IDS.wifiSetupCode}
                />
              </label>
            </Show>
            <WifiFields
              ssid={wifiSsid()}
              password={wifiPassword()}
              openNetwork={wifiOpenNetwork()}
              onSSID={setWifiSsid}
              onPassword={setWifiPassword}
              onOpenNetwork={setWifiOpenNetwork}
              required
            />
            <Show when={wifiFeedback()}>
              {(feedback) => (
                <div class={`setup-progress ${feedback().kind === 'failed' ? 'failed' : ''}`} role={feedback().kind === 'failed' ? 'alert' : 'status'}>
                  <span classList={{ pulse: wifiBusy() }}><Wifi size={19} /></span>
                  <div><strong>{feedback().message}</strong></div>
                </div>
              )}
            </Show>
            <button class="button button-primary button-large" type="submit" disabled={wifiBusy() || !wifiJoinEnabled()} data-testid={TEST_IDS.joinWifi}>
              {wifiBusy() ? 'Joining…' : wifiFeedback()?.kind === 'failed' ? 'Retry' : 'Join'}
            </button>
          </form>
        </ControlGate>
      </Modal>

      <ConfirmModal
        open={forgetWifiOpen()}
        title={`Forget ${wifiStatus()?.ssid || 'this network'}?`}
        confirmLabel="Forget this network"
        busyKey="forget-wifi"
        tone="danger"
        onConfirm={() => void forgetWifi()}
        onCancel={() => setForgetWifiOpen(false)}
      />

      <Modal title="Connect over Bluetooth" open={bluetoothControlOpen()} onClose={() => venue.actionBusy() !== 'connect-bluetooth' && setBluetoothControlOpen(false)}>
        <div class="setup-flow">
          <p>Enter the venue's setup code. A controller you've already connected to reconnects without asking again.</p>
          <label><span>Setup code</span><input type="password" autocomplete="off" value={bluetoothControlCode()} onInput={(event) => setBluetoothControlCode(event.currentTarget.value)} placeholder="Setup code" data-testid={TEST_IDS.bluetoothControlCode} /></label>
          <Show when={venue.bluetoothReason()}><InlineNotice tone="warning">{venue.bluetoothReason()}</InlineNotice></Show>
          <BusyButton busyKey="connect-bluetooth" class="button button-primary button-large" disabled={!bluetoothControlCode().trim()} onClick={() => void connectBluetoothControl()} data-testid={TEST_IDS.connectBluetooth}>
            <Bluetooth size={18} /> Connect
          </BusyButton>
        </div>
      </Modal>
    </>
  );
}
